'use client';

import type { SexSchema } from '@coas/validators/const';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useInvalidateAll } from '~/features/Cache';
import { useApplyDelta } from './mutations';
import { useCategoriesBySex, useIndicators } from './queries';
import type { IndicatorsStatus, TriAction } from './types';

export function useProductCategoriesDialog(productIds: string[], open: boolean) {
    const maleCatsQ = useCategoriesBySex('man' as const, open);
    const femaleCatsQ = useCategoriesBySex('woman' as const, open);

    const maleIds = useMemo(() => (maleCatsQ.data ?? []).map(c => c.id), [maleCatsQ.data]);
    const femaleIds = useMemo(() => (femaleCatsQ.data ?? []).map(c => c.id), [femaleCatsQ.data]);

    const maleIndQ = useIndicators({
        productIds,
        sexTab: 'man',
        categoryIds: maleIds,
        enabled: open && maleIds.length > 0,
    });
    const femaleIndQ = useIndicators({
        productIds,
        sexTab: 'woman',
        categoryIds: femaleIds,
        enabled: open && femaleIds.length > 0,
    });

    const maleStatusById = useMemo(() => {
        const m = new Map<string, IndicatorsStatus>();
        for (const c of maleIndQ.data?.categories ?? []) m.set(c.categoryId, c.status);
        return m;
    }, [maleIndQ.data]);

    const femaleStatusById = useMemo(() => {
        const m = new Map<string, IndicatorsStatus>();
        for (const c of femaleIndQ.data?.categories ?? []) m.set(c.categoryId, c.status);
        return m;
    }, [femaleIndQ.data]);

    const [maleActions, setMaleActions] = useState<Map<string, TriAction>>(new Map());
    const [femaleActions, setFemaleActions] = useState<Map<string, TriAction>>(new Map());

    const reset = useCallback(() => {
        setMaleActions(new Map());
        setFemaleActions(new Map());
    }, []);

    useEffect(() => {
        if (!open) reset();
    }, [open, reset]);

    const onChange = useCallback((tab: SexSchema, id: string, next: TriAction) => {
        const setter = tab === 'man' ? setMaleActions : setFemaleActions;
        setter(prev => {
            const m = new Map(prev);
            if (next === 'none') m.delete(id);
            else m.set(id, next);
            return m;
        });
    }, []);

    const { mutate: invalidateAll } = useInvalidateAll();

    const applyDelta = useApplyDelta();

    const submit = useCallback(async () => {
        const male = {
            add: [...maleActions].filter(([, v]) => v === 'add').map(([id]) => id),
            remove: [...maleActions].filter(([, v]) => v === 'remove').map(([id]) => id),
        };
        const female = {
            add: [...femaleActions].filter(([, v]) => v === 'add').map(([id]) => id),
            remove: [...femaleActions].filter(([, v]) => v === 'remove').map(([id]) => id),
        };
        if (male.add.length + male.remove.length + female.add.length + female.remove.length === 0) {
            toast.info('Не выбрано ни одного действия');
            return false;
        }
        toast.loading('Применяем изменения категорий...', { id: 'applyCats' });
        try {
            await applyDelta.mutateAsync({ productIds, male, female });
            toast.success('Категории обновлены', { id: 'applyCats' });
            invalidateAll();
            reset();
            return true;
        } catch {
            toast.error('Ошибка применения категорий', { id: 'applyCats' });
            return false;
        }
    }, [applyDelta, femaleActions, maleActions, productIds, reset, invalidateAll]);

    const isLoadingCats = maleCatsQ.isLoading || femaleCatsQ.isLoading;
    const isLoadingInd = maleIndQ.isLoading || femaleIndQ.isLoading;
    const isPending = applyDelta.isPending;

    const canSubmit = useMemo(() => {
        if (!productIds.length) return false;
        if (isLoadingCats || isLoadingInd || isPending) return false;
        return maleActions.size > 0 || femaleActions.size > 0;
    }, [productIds.length, isLoadingCats, isLoadingInd, isPending, maleActions.size, femaleActions.size]);

    const counts = maleIndQ.data?.counts ?? femaleIndQ.data?.counts ?? { male: 0, female: 0, unisex: 0 };

    return {
        male: { categories: maleCatsQ.data ?? [], statusById: maleStatusById, actions: maleActions },
        female: { categories: femaleCatsQ.data ?? [], statusById: femaleStatusById, actions: femaleActions },
        onChange,
        submit,
        canSubmit,
        isLoadingCats,
        isLoadingInd,
        isPending,
        counts,
    };
}
